import { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import { formatPhoneNumber } from '../../../../Utils/phoneUtils';
import { useLanguage } from '../../../../Context/LanguageContext';

// Model //
import UserNameModifyModal from './Modal/UserNameModifyModal';
import DeleteWarningModal from './Modal/DeleteWarningModal';
import TermsEditModal from './Modal/TermsEditModal';
import TermsViewModal from './Modal/TermsViewModal';
import FetchModule from '../../../Share/Network/FetchModule';
// Model //

// Component //
import TextArea from '../../../../Component/MemberComponent/MemberDetail/TextArea';
// Component //

// Image //
import WhiteTrashIcon from '../../../../image/WhiteTrashIcon.png';
import DefaultProfile from '../../../../image/DefaultProfile.png';
import PencelImg from '../../../../image/Pencel.png';
import { ReactComponent as DocumentSvg } from '../../../../image/Document.svg';
// Image //

const IndividualInfo = forwardRef((props, ref) => {
  const {
    terms,
    setTerms,
    provisions,
    setProvisions,
    defaultProvisions,
    memberData,
    originalMemberDetail,
    setOriginalMemberDetail,
    recentlyLocker,
    rendering
  } = props;

  const history = useHistory();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t, languageMode } = useLanguage();

  const [defaultVal, setDefaultVal] = useState(memberData);
  const [memberDetail, setMemberDetail] = useState(memberData.memberDetail);
  const [isClickedModifyUserName, setIsClickedModifyUserName] = useState(false);
  const [isClickedDeleteWarning, setIsClickedDeleteWarning] = useState(false);
  const [isProfileError, setIsProfileError] = useState(false);
  const [isTermsButtonHover, setIsTermsButtonHover] = useState(false);
  const [isViewEditTerms, setIsViewEditTerms] = useState(false);
  const [isViewTerms, setIsViewTerms] = useState(false);
  const [isSavingProvision, setIsSavingProvision] = useState(false);

  useImperativeHandle(ref, () => ({
    setMemberData: (val) => {
      setDefaultVal(val);
    }
  }));

  useEffect(() => {
    setMemberDetail(memberData.memberDetail);
  }, [memberData])

  // 유저 프로필 결정 //
  function decideUserProfile() {
    if (isProfileError || defaultVal.profile === '') return DefaultProfile;
    else return defaultVal.profile;
  }

  // 약관 버튼 테두리 선택 //
  function decideTermsButtonBorder() {
    if (isTermsButtonHover) {
      if (defaultVal.termsId === undefined) return `1px dashed ${theme.subText}`;
      else if (defaultVal.termsAgreementId === undefined) return `1px solid ${theme.subText}`;
      else return `1px solid ${theme.subKeyHover}`;
    }
    else {
      if (defaultVal.termsId === undefined) return `1px dashed ${theme.line}`;
      else if (defaultVal.termsAgreementId === undefined) return `1px solid ${theme.line}`;
      else return `1px solid ${theme.subKey}`;
    }
  }

  // 약관 버튼 배경 선택 //
  function decideTermsButtonBackgroundColor() {
    if (defaultVal.termsId === undefined) return theme.subBackground;
    else {
      if (isTermsButtonHover) {
        if (defaultVal.termsAgreementId === undefined) return theme.subText;
        else return theme.subKeyHover;
      }
      else {
        if (defaultVal.termsAgreementId === undefined) return theme.line;
        else return theme.subKey;
      }
    }
  }

  // 약관 버튼 텍스트 선택 //
  function decideTermsButtonText() {
    if (defaultVal.termsId === undefined) return t('memberDetail.termsModal.termsNotReady');
    else if (defaultVal.termsAgreementId === undefined) return t('memberDetail.termsModal.termsUnsigned');
    else return t('memberDetail.termsModal.termsSigned');
  }

  // 약관 버튼 텍스트 색상 선택 //
  function decideTermsButtonTextColor() {
    if (defaultVal.termsId === undefined) return theme.subText;
    else return theme.subBackground;
  }

  // 약관 버튼 아이콘 색상 선택 //
  function decideTermsButtonIconColor() {
    if (defaultVal.termsId === undefined) return theme.subText; // 약관 준비전
    else if (defaultVal.termsAgreementId === undefined) return theme.white; // 서명 미완료
    else return theme.subBackground; // 서명 완료
  }

  // 회원권 삭제 클릭시 //
  function onClickMemberInfoDelete() {
    let requestData = {
      memberId: memberData.memberId
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('member/delete', 'POST', requestData);
        if (responseData.status === 200) {
          alert(t('memberDetail.alertMemberDeleted'));
          history.push('/member/valid');
        }
        else if (responseData.status === 201) {
          alert(t('memberDetail.alertInvalidMember'));
          history.push('/member/valid');
        }
        else if (responseData.status === 202) {
          alert(t('memberDetail.alertActiveMembership'));
        }
        else if (responseData.status === 203) {
          alert(t('memberDetail.alertActiveLocker'));
        }
        else if (responseData.status === 204) {
          alert(t('memberDetail.alertMemberNotExists'));
          history.push('/member/valid');
        }
        else if (responseData.status === 401) {
          alert(t('memberDetail.alertLoginRequired'));
          history.push('/');
        }
      }
    )();
  }

  function onBlurTextArea() {
    if (originalMemberDetail !== memberDetail) {

      let data = {
        storeId: window.sessionStorage.getItem('StoreId'),
        memberId: memberData.memberId,
        memberDetail: memberDetail
      }
      
      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postData('member/modify/member_detail', 'POST', data);
          
          if (responseData.status === 200) {
            setOriginalMemberDetail(memberDetail);
            rendering();
          }
        }
      )();
    }
  }

  // 변경된 이름 저장하기 클릭시 //
  function onClickSave(changeProfile, changeName, changeDate, changeGender, changePhone, changeCountryCode) {
    let data = {
      memberId: memberData.memberId,
      profile: changeProfile,
      userName: changeName,
      birthDay: changeDate,
      gender: changeGender,
      phone: changePhone,
      countryCode: changeCountryCode
    }
    
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postData('member/modify/username', 'POST', data);
        if (responseData.status === 200) {
          alert(t('memberDetail.alertInfoSaved'));
          setIsClickedModifyUserName(false);
          rendering();
        }
        else if (responseData.status === 201) {
          alert(t('memberDetail.alertNoPermission'));
          history.push('/member/valid');
        }
        else if (responseData.status === 401) {
          alert(t('memberDetail.alertLoginRequired'));
          history.push('/');
        }
      }
    )();
  }

  // 약관 버튼 클릭시 //
  function onClickTermsButton() {
    if (defaultVal.termsId === undefined) setIsViewEditTerms(true);
    else setIsViewTerms(true);
  }

  // 약관 저장하기 클릭시 //
  function onClickSaveTerms(data) {
    if (data.title === '') {
      alert(t('memberDetail.termsModal.alertTermsNameRequired'));
      return;
    }
    else if (data.provisionKeys.length === 0) {
      alert(t('memberDetail.termsModal.alertTermsProvisionRequired'));
      return;
    }
    else if (isSavingProvision) {
      alert(t('memberDetail.termsModal.alertTermsSaving'));
      return;
    } 

    setIsSavingProvision(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('terms/save', 'POST', data);
        
        if (responseData.status === 200) {
          setTerms(responseData.data.terms);
          setProvisions(responseData.data.provisions);
          setIsViewEditTerms(false);
          rendering();
          setIsSavingProvision(false);
        }
        else {
          alert(t('memberDetail.termsModal.alertTermsSaveFailed'));
          setIsSavingProvision(false);
        }
      }
    )();
  }

  // 약관 닫기 클릭시 //
  function onClickExitEditTerms() {
    setIsViewEditTerms(false);
  }

  // 약관 닫기 클릭시 //
  function onClickExitViewTerms() {
    setIsViewTerms(false);
  }

  return (
    <TopLevelWrapper>
      { 
        isClickedModifyUserName && 
        <UserNameModifyModal
          profile={defaultVal.profile}
          userName={defaultVal.name}
          birthDay={defaultVal.birthDay}
          gender={defaultVal.gender}
          phone={defaultVal.phone}
          countryCode={defaultVal.countryCode}
          onClickSave={onClickSave}
          onClickClose={() => setIsClickedModifyUserName(false)}/> 
      }
      { isClickedDeleteWarning && <DeleteWarningModal onClickExit={() => setIsClickedDeleteWarning(false)} onClickDelete={onClickMemberInfoDelete}/> }
      {
        isViewTerms &&
          <TermsViewModal
            termsAgreementId={defaultVal.termsAgreementId}
            storeName={defaultVal.storeName}
            clickedTerms={defaultVal.terms}
            defaultProvisions={defaultProvisions} 
            provisions={provisions}
            onClickExit={onClickExitViewTerms} />
      }
      { 
        isViewEditTerms && 
          <TermsEditModal 
            storeName={defaultVal.storeName}
            clickedTerms={defaultVal.terms}
            defaultProvisions={defaultProvisions} 
            provisions={provisions}
            onClickExit={onClickExitEditTerms}
            onClickSave={onClickSaveTerms} /> 
      }
      <UserPhotoWrapper $isDark={isDark}>
        <UserPhoto src={decideUserProfile()} onError={() => setIsProfileError(true)}/>
      </UserPhotoWrapper>
      <UserInfoView $isDark={isDark}>
        <TopButtonRow>
          <TermsButton
            border={decideTermsButtonBorder()}
            backgroundColor={decideTermsButtonBackgroundColor()}
            onMouseOver={() => setIsTermsButtonHover(true)}
            onMouseLeave={() => setIsTermsButtonHover(false)}
            onClick={onClickTermsButton}>
            <StyledTermsIcon color={decideTermsButtonIconColor()} />
            <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={decideTermsButtonTextColor()} cursor='pointer'>{decideTermsButtonText()}</Text>
          </TermsButton>
          <DeleteButton onClick={() => setIsClickedDeleteWarning(true)}>
            <DeleteIcon src={WhiteTrashIcon}/>
          </DeleteButton>
        </TopButtonRow>
        <ContentRow>
          <InfoBoxWrapper>
            <UserNameWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} cursor='default'>{defaultVal.name}</Text>
              <NameModifyButton onClick={() => setIsClickedModifyUserName(true)}>
                <Pencel src={PencelImg}/>
              </NameModifyButton>
            </UserNameWrapper>
              <InfoItem>
                <InfoLabel><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.labelGender')}</Text></InfoLabel>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{defaultVal.gender === 'male' ? t('memberDetail.male') : t('memberDetail.female')}</Text>
              </InfoItem>
              <InfoItem>
                <InfoLabel><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.labelBirth')}</Text></InfoLabel>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{defaultVal.birthDay ? moment(defaultVal.birthDay).format(t('calendarInput.fullDateFormat')) : ''}</Text>
              </InfoItem>
              <InfoItem>
                <InfoLabel><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.labelPhone')}</Text></InfoLabel>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{defaultVal.phone === '' ? t('memberDetail.noPhone') : formatPhoneNumber(defaultVal.phone, defaultVal.countryCode, languageMode)}</Text>
              </InfoItem>
              <InfoItem>
                <InfoLabel><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.labelId')}</Text></InfoLabel>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{defaultVal.nickName}</Text>
              </InfoItem>
              <InfoItem>
                <InfoLabel><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('memberDetail.labelLocker')}</Text></InfoLabel>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText} cursor='default'>{recentlyLocker}</Text>
              </InfoItem>
          </InfoBoxWrapper>
          <ColumnLayerWrapper>
            <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text} cursor='default'>{t('memberDetail.memberNotes')}</Text>
            <MemberNoticeWindow>
              <TextArea
                value={memberDetail}
                setValue={setMemberDetail}
                onBlur={onBlurTextArea}
                placeholder={t('memberDetail.memberNotesPlaceholder')}/>
            </MemberNoticeWindow>
          </ColumnLayerWrapper>
        </ContentRow>
      </UserInfoView>
    </TopLevelWrapper>
  )
})

export default IndividualInfo;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 30px;

  transition: all 0.3s ease-in-out;
`;

const UserPhotoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 229px;
  height: 229px;

  overflow: hidden;

  border: 1px solid ${props => props.theme.line};
  border-radius: 24px;

  background-color: ${props => props.theme.subBackground};
`;

const UserPhoto = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

const UserInfoView = styled.div`
  display: flex;
  flex-direction: column;

  width: 711px;
  height: 229px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
`;

const TopButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
  height: 37px;
  flex-shrink: 0;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: flex-start;

  padding: 0 40px;
  gap: 20px;
  
  box-sizing: border-box;
  flex: 1;
`;

const InfoBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: flex-start;

  width: 190px;
  height: 170px;

  gap: 8px;
`;

const ColumnLayerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 421px;
  height: 170px;
  box-sizing: border-box;

  gap: 8px;
`;

const InfoGridWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 10px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;
  height: 21px;

  gap: 10px;
`;

const InfoLabel = styled.div`
  width: 60px;
  flex-shrink: 0;
`;

const UserNameWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 24px;

  gap: 10px;

  & p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 55px;
  height: 37px;

  border-radius: 0px 20px 0px 0px;
  background-color: ${props => props.theme.pointRedAlpha50};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.pointRedHover};
  }
`;

const DeleteIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const TermsButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0px 10px;

  height: 37px;

  gap: 5px;

  border: ${props => props.border};
  border-radius: 0px 0px 0px 5px;
  box-sizing: border-box;
  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const StyledTermsIcon = styled(DocumentSvg)`
  display: block;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  overflow: visible;

  path {
    stroke: ${({ color }) => color};
  }
`;

const MemberNoticeWindow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 15px;
  margin: 0 0 0 0;

  box-sizing: border-box;
  width: 421px;
  height: 138px;

  overflow-x: hidden;
  overflow-y: scroll;

  border: 1px solid ${props => props.theme.line};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  &::-webkit-scrollbar {
    height: 4px;
    width: 0px;
  }

  &::-webkit-scrollbar-thumb {
		border: 2px solid transparent;
		background-color: ${props => props.theme.scrollBar};
		border-radius: 200px;
	}

  &::-webkit-scrollbar-track {
		background-color: ${props => props.theme.subBackground};
		border-radius: 200px;
	}
`;

const NameModifyButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;

const Pencel = styled.img`
  width: 16px;
  height: 16px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
